import { cloneDeep } from 'lodash-es'
import type { KeysMatching, NonEmptyArray } from '~/types/helpers'
import { isDev } from '~/helper/nuxt'

type Mutable<T> = {
  -readonly [K in keyof T]: T[K]
}

export function copy<T extends Exclude<object | any[], undefined>>(obj: T): Mutable<T> {
  return JSON.parse(JSON.stringify(obj))
}
export function copy2<T>(obj: T): T {
  return cloneDeep(obj)
}

/**
 * Syntactic sugar for Promise + setTimeout
 */
export function wait(ms: number, callback?: () => unknown | Promise<unknown>): Promise<void> {
  return new Promise((resolve) =>
    setTimeout(async () => {
      await callback?.()
      resolve()
    }, ms),
  )
}
export const sleep = wait

/**
 * Provide a proxy of a function with caching
 *
 * @example
 * ```TypeScript
 * const fetchCached = asCached(fetch) // create cache
 * const result = fetchCached('https://example.com') // call underlying fetch with key
 * ...
 * fetchCached.clear() // clear cache later
 * ```
 */
export function asCached<K, V>(factory: (arg: K) => V) {
  const cache = new Map<K, V>()
  return {
    get(key: K): V {
      if (cache.has(key)) {
        if (isDev) {
          console.info(`Cache hit for: ${key}`)
          // console.groupCollapsed(`Cache hit for: ${key}`)
          // console.trace()
          // console.groupEnd()
        }

        return cache.get(key) as V
      }
      const value = factory(key)
      cache.set(key, value)
      // Evict rejected promises: a transient failure (e.g. one 502 on an asset
      // fetch) must not poison the cache for the rest of the session
      Promise.resolve(value).catch(() => cache.delete(key))
      return value
    },
    clear() {
      cache.clear()
    },
  }
}

export function debounce<T extends (...args: any) => any>(callback: T, delay: number) {
  let timeoutTimer: ReturnType<typeof setTimeout>

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutTimer)

    timeoutTimer = setTimeout(() => {
      callback(...args)
    }, delay)
  }
}

/**
 * Used to convert, for example, an Image to its base64 data url representation
 * @example
 *  <image src="data:image/png;base64,kjqncvio12">
 *
 * @see
 *  https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
 */
export function blob2base64(blob: Blob): Promise<Base64DataUrl> {
  return new Promise((resolve, _) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as Base64DataUrl)
    reader.readAsDataURL(blob)
  })
}
export type Base64DataUrl = `data:${string};base64,${string}`

export function blob2text(blob: Blob): Promise<string> {
  return new Promise((resolve, _) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.readAsText(blob)
  })
}

/**
 * Check if the File MIME-type or extension matches the accept
 *
 * Wildcard MIME-types are allowed, e.g. "image/*"
 */
export function fileMatchesAccept(file: File, accept: string | string[]): boolean {
  const _accept = Array.isArray(accept) ? accept : [accept].map((a) => a.split(',')).flat()
  return _accept.some((accepting) => {
    // Extension
    if (accepting.startsWith('.')) {
      return file.name.endsWith(accepting)
    }
    // MIME-type wildcard
    if (accepting.endsWith('*')) {
      return file.type.startsWith(accepting.split('/')[0])
    }
    // MIME-type
    return accepting === file.type
  })
}

export function filterNullable<T>(item: T): item is NonNullable<T> {
  return item !== null && item !== undefined
}

export function isNotEmpty<T>(list: T[]): list is NonEmptyArray<T> {
  return list.length > 0
}

export const isTruthy = filterNullable

export function sortBy<T, K extends KeysMatching<T, string>>(list: T[], key: K, direction: 'asc' | 'desc' = 'asc') {
  const arr = list.sort((a, b) => {
    const valueA = a[key] as string
    const valueB = b[key] as string
    return valueA.localeCompare(valueB)
  })
  return direction === 'desc' ? arr.reverse() : arr
}

export function truncate(str: string | null | undefined, maxLength: number = 25, ellipsis: boolean = true): string {
  if (typeof str !== 'string') {
    return ''
  }
  return str.length > maxLength ? `${str.slice(0, maxLength)}${ellipsis ? '...' : ''}` : str
}

export function getCopyName(baseName: string, existingNames: string[]): string {
  let counter = 1
  let newName = baseName

  // Solange der Name schon existiert, hochzählen
  while (existingNames.includes(newName)) {
    counter++
    newName = `${baseName} (${counter})`
  }

  return newName
}

type ByteUnit = 'kb' | 'mb' | 'gb'
type ByteValue = `${string} ${ByteUnit}`

/**
 * Formats a byte value in an auto-selected or forced unit.
 * Numeric conversion stays binary (1024-based).
 */
export function formatBytes(bytes?: number | null, target?: ByteUnit, precision = 2, human = false): ByteValue {
  const normalizedPrecision = Math.max(0, precision)
  const units: Record<ByteUnit, number> = {
    kb: 1024,
    mb: 1024 ** 2,
    gb: 1024 ** 3,
  }

  if (!bytes || bytes <= 0) {
    return `0 ${target ?? 'kb'}`
  }

  function formatUnit(unit: ByteUnit): ByteValue {
    const value = (bytes as number) / units[unit]

    if (human && value < 1) {
      return `> 1 ${unit}`
    }

    return `${value.toFixed(normalizedPrecision)} ${unit}`
  }

  if (target) {
    return formatUnit(target)
  }

  if (bytes < units.mb) {
    return formatUnit('kb')
  }

  if (bytes < units.gb) {
    return formatUnit('mb')
  }

  return formatUnit('gb')
}

export function toggleInSet<T>(set: Set<T>, value: T, to?: boolean) {
  if (to !== undefined) {
    if (to) return set.add(value)
    if (!to) return set.delete(value)
  }

  if (set.has(value)) {
    return set.delete(value)
  } else {
    return set.add(value)
  }
}

export function mapPush<K, V>(map: Map<K, V[]>, key: K, value: V): V[] {
  const array = map.get(key) || []
  array.push(value)
  if (!map.has(key)) map.set(key, array)
  return array
}

export function formatBooleanSymbol(value: any) {
  return value ? '✓' : '✗'
}
